import { useEffect, useState } from "react";

import { useSearchParameters } from "@/components/signup/hooks";
import {
    ufoEventRedirectToProduct,
    UfoCustomTimingKey,
    UfoCustomTimingWrapper
} from "@/components/signup/utils";
import { useUserContext } from "@/components/user-context";
import { DataFoundationsAttributes } from "@/templates/ondemand/signup/confirmation/confirmation-page/src/views/user-seg-bxp-ui/types.ts";
import { DataFoundationsWrapper } from "@/utils/data-foundations/data-foundation-types";

import { LoadingState } from "../../components/loading-state/loading-state";
import { useJiraTemplateChoice } from "../../hooks/use-jira-template-choice";
import { useScreenManager } from "../../hooks/use-screen-manager";
import { getPrimaryProduct } from "../../hooks/utils/use-cloud-status-utils";

import { TemplateChoiceScreen } from "./template-choice-screen";

const JiraTemplateChoiceViewManager = () => {
    const { skipScreen } = useScreenManager();
    const { loadingUserSegmentData, shouldShowJiraTemplateChoiceScreen } =
        useJiraTemplateChoice();

    const { products, edition, jtbd, teamType } = useSearchParameters();
    const productKey = getPrimaryProduct(products);
    const { profile } = useUserContext();
    const userType = profile.cohorts?.userType;
    const domainType = profile.cohorts?.domainType;
    const companyType = profile.cohorts?.companyType;
    const [waitForAttributes, setWaitForAttributes] = useState(true);
    const [dataFoundationAttributes, setDataFoundationAttributes] =
        useState<DataFoundationsAttributes>({
            productKey,
            edition,
            teamType
        });

    useEffect(() => {
        if (!profile.requestInProgress) {
            setDataFoundationAttributes((prevAttributes) => ({
                ...prevAttributes,
                userType: userType,
                domainType: domainType,
                companyType: companyType
            }));
            setWaitForAttributes(false);
        }
    }, [profile.requestInProgress, userType, domainType, companyType]);

    // Skip template choice screen if a jtbd query parameter exists (even if its an empty array)
    useEffect(() => {
        if (jtbd) {
            skipScreen();
        }
    }, [jtbd]);

    useEffect(() => {
        // on first render, transition to next screen if it should not be shown.
        // for control, this screen can be hidden for users that fall into business teams
        if (!shouldShowJiraTemplateChoiceScreen && !loadingUserSegmentData) {
            skipScreen();
        }
    }, [
        loadingUserSegmentData,
        shouldShowJiraTemplateChoiceScreen,
        skipScreen
    ]);

    if (loadingUserSegmentData) {
        return <LoadingState />;
    }

    // do not load UI if the screen should be hidden AND the user is not in
    // Transparent Template For All treatment
    if (!shouldShowJiraTemplateChoiceScreen) {
        return null;
    }

    // Do not load UI if jtbd is set
    if (jtbd) {
        return null;
    }

    return (
        <DataFoundationsWrapper
            dataFoundationsEventOption="TemplateChoiceScreenViewed"
            attributes={dataFoundationAttributes}
            waitForAttributes={waitForAttributes}
        >
            <TemplateChoiceScreen />
        </DataFoundationsWrapper>
    );
};

/**
 * UFO Wrapper to capture Signup's FE Performance metrics & "Product Landing" SLOs
 *   - https://go/signup-perf
 **/
const JiraTemplateChoiceUfoWrapper = () => {
    return (
        <UfoCustomTimingWrapper
            ufoEvent={ufoEventRedirectToProduct}
            ufoCustomTimingKey={
                UfoCustomTimingKey.JIRA_TEMPLATE_CHOICE_DWELL_TIME
            }
        >
            <JiraTemplateChoiceViewManager />
        </UfoCustomTimingWrapper>
    );
};

export default JiraTemplateChoiceUfoWrapper;
